/**
 * A custom element that renders a live countdown to a target date/time.
 *
 * Reads the target moment from the `deadline` attribute, either:
 * - an ISO 8601 string without offset, interpreted as wall-clock time in the
 *   IANA zone given by the `timezone` attribute (falling back to the visitor's
 *   local zone), or
 * - Unix epoch seconds (e.g. Liquid's `date: '%s'` on a date_time metafield),
 *   which is already an absolute instant, so `timezone` is ignored.
 * Ticks once per second and, once the deadline is reached, freezes the display
 * at 00:00:00:00 rather than counting up into negative values.
 *
 * Opt-in `hide-on-expire` attribute: the element is hidden while the deadline
 * has passed (or can't be parsed) and shown while it's still in the future, so
 * it can be server-rendered `hidden` without flashing zeros before JS runs.
 *
 * Optional `starts` attribute (same formats as `deadline`): the element stays
 * hidden until that moment, then reveals itself on the next tick, so a countdown
 * can be scheduled ahead of time. Ignored if missing or unparseable.
 */
class AnnouncementCountdown extends HTMLElement {
  #intervalId = null;

  connectedCallback() {
    const deadline = this.getAttribute('deadline');
    const timezone = this.getAttribute('timezone');
    this.#deadline = deadline ? parseDeadline(deadline, timezone) : NaN;
    const starts = this.getAttribute('starts');
    this.#starts = starts ? parseDeadline(starts, timezone) : NaN;

    // If the deadline is unparseable, leave the server-rendered fallback in place.
    if (Number.isNaN(this.#deadline)) return;

    this.#daysEl = this.querySelector('[data-unit="days"]');
    this.#hoursEl = this.querySelector('[data-unit="hours"]');
    this.#minutesEl = this.querySelector('[data-unit="minutes"]');
    this.#secondsEl = this.querySelector('[data-unit="seconds"]');

    // Start the interval before the first tick so an already-expired deadline
    // clears it immediately instead of ticking once more.
    this.#intervalId = window.setInterval(this.#tick, 1000);
    this.#tick();
  }

  disconnectedCallback() {
    if (this.#intervalId !== null) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #deadline = NaN;
  #starts = NaN;
  #daysEl = null;
  #hoursEl = null;
  #minutesEl = null;
  #secondsEl = null;

  #tick = () => {
    const now = Date.now();
    // Clamp to zero so the countdown stops at 00:00:00:00 instead of going negative.
    const remaining = Math.max(0, this.#deadline - now);

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (this.#daysEl) this.#daysEl.textContent = pad(days);
    if (this.#hoursEl) this.#hoursEl.textContent = pad(hours);
    if (this.#minutesEl) this.#minutesEl.textContent = pad(minutes);
    if (this.#secondsEl) this.#secondsEl.textContent = pad(seconds);

    // Re-applied every tick so the element recovers if a DOM morph restores the
    // server-rendered `hidden` attribute while the timer is still running.
    // NaN `#starts` (no/unparseable `starts`) compares false, so it never hides.
    const notStarted = now < this.#starts;
    const expired = remaining === 0 && this.hasAttribute('hide-on-expire');
    if (this.hasAttribute('starts') || this.hasAttribute('hide-on-expire')) {
      this.hidden = notStarted || expired;
    }

    // Once we hit zero, stop ticking — the display is already frozen at 00:00:00:00.
    if (remaining === 0) this.disconnectedCallback();
  };
}

/**
 * Zero-pads a number to at least two digits.
 * @param {number} value
 * @returns {string}
 */
function pad(value) {
  return value.toString().padStart(2, '0');
}

/**
 * Resolves a deadline to an epoch timestamp. Epoch seconds ("1798725600") pass
 * straight through. An offset-less ISO string ("2026-08-15T00:00:00") is treated
 * as wall-clock time in the given IANA timezone, falling back to the visitor's
 * local zone when no/invalid timezone is given.
 * @param {string} deadline
 * @param {string | null} timezone
 * @returns {number} epoch milliseconds, or NaN if unparseable
 */
function parseDeadline(deadline, timezone) {
  if (/^\d+$/.test(deadline)) return Number(deadline) * 1000;

  const match = deadline.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
  if (!match || !timezone) return new Date(deadline).getTime();

  const [, year, month, day, hour, minute, second] = match.map(Number);
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);

  try {
    // Guess the epoch by treating the wall time as UTC, then correct by the
    // zone's offset at that instant. A second pass handles the case where the
    // correction itself crosses a DST transition.
    let epoch = asUtc;
    for (let i = 0; i < 2; i++) {
      epoch = asUtc - timezoneOffset(epoch, timezone);
    }
    return epoch;
  } catch {
    // Unknown timezone identifier — fall back to the visitor's local zone.
    return new Date(deadline).getTime();
  }
}

/**
 * Returns how far (in ms) the given timezone is ahead of UTC at an instant.
 * @param {number} epochMs
 * @param {string} timezone
 * @returns {number}
 */
function timezoneOffset(epochMs, timezone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(epochMs));

  const value = (type) => Number(parts.find((part) => part.type === type).value);
  // hourCycle quirk: some engines report midnight as "24".
  const wallAsUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour') % 24,
    value('minute'),
    value('second')
  );

  return wallAsUtc - epochMs;
}

// Register once
if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
